import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Fade,
  Stack,
  Paper,
  LinearProgress,
  Tooltip,
  Grow,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  VpnKeyOutlined,
  CheckCircleOutline,
  ErrorOutline,
  KeyboardArrowRight,
  SecurityOutlined,
} from '@mui/icons-material';
import { useTheme, styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useChangePasswordMutation } from '../../services/authApi';
import { useErrorModal } from '../../hooks/useErrorModal';
import { updateUser } from '../../store';
import ProtectedRoute from '../../components/ProtectedRoute';

// Custom Styled Components for Premium Feel
const GlassCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '24px',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 8px 32px 0 rgba(117, 34, 209, 0.08)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': {
    boxShadow: '0 12px 48px 0 rgba(117, 34, 209, 0.12)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    transition: '0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}10`,
    },
  },
}));

const ChangePassword = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isForced = location.state?.forced || false;

  const [changePasswordMutation, { isLoading }] = useChangePasswordMutation();
  const { showError, ErrorModal } = useErrorModal();

  // Password strength calculation logic
  useEffect(() => {
    const password = formData.newPassword;
    let strength = 0;
    if (password.length >= 8) strength += 25;
    if (/[A-Z]/.test(password)) strength += 25;
    if (/[0-9]/.test(password)) strength += 25;
    if (/[^A-Za-z0-9]/.test(password)) strength += 25;
    setPasswordStrength(strength);
  }, [formData.newPassword]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
    setSuccess('');
  };

  const toggleVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswords = () => {
    if (formData.currentPassword.length < 1) {
      setError('Current password is required');
      return false;
    }
    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return false;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData.currentPassword === formData.newPassword) {
      setError('New password cannot be the same as current password');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswords()) return;

    try {
      const result = await changePasswordMutation({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      }).unwrap();

      if (result.success) {
        setSuccess('Security credentials updated successfully!');
        if (result.data?.employee) {
          dispatch(updateUser(result.data.employee));
        }
        
        setTimeout(() => {
          if (isForced) {
            navigate('/dashboard');
          } else {
            setSuccess('');
            setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
          }
        }, 2000);
      }
    } catch (err) {
      showError(err);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 25) return theme.palette.error.main;
    if (passwordStrength <= 50) return theme.palette.warning.main;
    if (passwordStrength <= 75) return theme.palette.info.main;
    return theme.palette.success.main;
  };

  return (
    <ProtectedRoute>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F4EBFF',
          backgroundImage: `radial-gradient(#7522D115 1.5px, transparent 1.5px)`,
          backgroundSize: '32px 32px',
          p: 3,
        }}
      >
        <Grow in={true} timeout={800}>
          <Box sx={{ width: '100%', maxWidth: 480 }}>
            <GlassCard elevation={0}>
              <Stack spacing={4}>
                {/* Header Section */}
                <Stack alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '16px',
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                    }}
                  >
                    <SecurityOutlined sx={{ fontSize: 32 }} />
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#111', letterSpacing: '-0.02em', mb: 1 }}>
                      {isForced ? 'Secure Your Account' : 'Update Password'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', maxWidth: 320, mx: 'auto' }}>
                      {isForced 
                        ? 'For security reasons, please set a new password to continue.' 
                        : 'Choose a strong password to keep your account safe.'}
                    </Typography>
                  </Box>
                </Stack>

                {/* Status Messages */}
                <Fade in={!!error || !!success}>
                  <Box>
                    {error && (
                      <Alert 
                        severity="error" 
                        icon={<ErrorOutline />}
                        sx={{ borderRadius: '12px', fontSize: '13px', border: '1px solid #fee2e2' }}
                      >
                        {error}
                      </Alert>
                    )}
                    {success && (
                      <Alert 
                        severity="success" 
                        icon={<CheckCircleOutline />}
                        sx={{ borderRadius: '12px', fontSize: '13px', border: '1px solid #dcfce7' }}
                      >
                        {success}
                      </Alert>
                    )}
                  </Box>
                </Fade>

                {/* Form Section */}
                <form onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Stack spacing={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', ml: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Current Password
                      </Typography>
                      <StyledTextField
                        fullWidth
                        name="currentPassword"
                        type={showPasswords.current ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                        placeholder="••••••••"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined sx={{ color: '#ccc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => toggleVisibility('current')} edge="end">
                                {showPasswords.current ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', ml: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        New Password
                      </Typography>
                      <StyledTextField
                        fullWidth
                        name="newPassword"
                        type={showPasswords.new ? 'text' : 'password'}
                        value={formData.newPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                        placeholder="••••••••"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <VpnKeyOutlined sx={{ color: '#ccc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => toggleVisibility('new')} edge="end">
                                {showPasswords.new ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      
                      {/* Strength Indicator */}
                      {formData.newPassword && (
                        <Box sx={{ mt: 1, px: 0.5 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: getStrengthColor() }}>
                              Strength: {passwordStrength <= 25 ? 'Weak' : passwordStrength <= 50 ? 'Fair' : passwordStrength <= 75 ? 'Good' : 'Strong'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#999' }}>{passwordStrength}%</Typography>
                          </Stack>
                          <LinearProgress 
                            variant="determinate" 
                            value={passwordStrength} 
                            sx={{ 
                              height: 6, 
                              borderRadius: 3,
                              backgroundColor: '#eee',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getStrengthColor(),
                                borderRadius: 3,
                              }
                            }}
                          />
                        </Box>
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#333', ml: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Confirm New Password
                      </Typography>
                      <StyledTextField
                        fullWidth
                        name="confirmPassword"
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        disabled={isLoading}
                        placeholder="••••••••"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CheckCircleOutline sx={{ color: '#ccc', fontSize: 20 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={() => toggleVisibility('confirm')} edge="end">
                                {showPasswords.confirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isLoading || (formData.newPassword && passwordStrength < 50)}
                      endIcon={!isLoading && <KeyboardArrowRight />}
                      sx={{
                        py: 1.8,
                        mt: 2,
                        borderRadius: '14px',
                        backgroundColor: theme.palette.primary.main,
                        fontSize: '15px',
                        fontWeight: 700,
                        textTransform: 'none',
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.dark,
                          boxShadow: `0 8px 20px ${theme.palette.primary.main}30`,
                          transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm Password Change'}
                    </Button>

                    {!isForced && (
                      <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/dashboard')}
                        sx={{ 
                          color: '#666', 
                          fontWeight: 600,
                          fontSize: '14px',
                          textTransform: 'none',
                          '&:hover': { backgroundColor: '#F4EBFF', color: theme.palette.primary.main }
                        }}
                      >
                        Cancel and return to dashboard
                      </Button>
                    )}
                  </Stack>
                </form>
              </Stack>
            </GlassCard>
          </Box>
        </Grow>
        <ErrorModal />
      </Box>
    </ProtectedRoute>
  );
};

export default ChangePassword;

